import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Grid,
  Button,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Edit as EditIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import TrainerProfileForm from '../components/TrainerProfileForm';
import api from '../utils/api';

const TrainerProfilePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/trainer-profiles/me');
      
      if (response.data.success) {
        setProfile(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching trainer profile:', err);
      if (err.response?.status === 404) {
        // Profile doesn't exist yet
        setProfile(null);
      } else {
        setError(err.response?.data?.error || t('trainerProfile:errors.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = (savedProfile) => {
    setProfile(savedProfile);
    setEditing(false);
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? t('trainerProfile:time.pm') : t('trainerProfile:time.am');
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (user?.role !== 'trainer') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          {t('trainerProfile:errors.accessDenied')}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('trainerProfile:loading')}
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  if (editing || !profile) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <TrainerProfileForm
          profile={profile}
          onSave={handleSaveProfile}
          onCancel={profile ? () => setEditing(false) : null}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('trainerProfile:title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setEditing(true)}
        >
          {t('trainerProfile:actions.editProfile')}
        </Button>
      </Box>

      {/* Basic Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('trainerProfile:sections.basicInfo')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{xs:12 , sm:6}}>
              <Typography variant="body2" color="text.secondary">
                {t('common:labels.name')}
              </Typography>
              <Typography variant="body1">
                {profile.user.firstName} {profile.user.lastName}
              </Typography>
            </Grid>
            <Grid size={{xs:12 , sm:6}}>
              <Typography variant="body2" color="text.secondary">
                {t('common:labels.email')}
              </Typography>
              <Typography variant="body1">
                {profile.user.email}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Specializations */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('trainerProfile:sections.specializations')}
          </Typography>
          <Box>
            {profile.specializations.map((spec, index) => (
              <Chip
                key={index}
                label={spec}
                sx={{ mr: 1, mb: 1 }}
                color="primary"
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Bio */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('trainerProfile:sections.bio')}
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {profile.bio}
          </Typography>
        </CardContent>
      </Card>

      {/* Weekly Availability */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('trainerProfile:sections.weeklyAvailability')}
          </Typography>
          <Grid container spacing={2}>
            {profile.weeklyAvailability
              .sort((a, b) => {
                const days = [
                  t('trainerProfile:days.monday'),
                  t('trainerProfile:days.tuesday'), 
                  t('trainerProfile:days.wednesday'),
                  t('trainerProfile:days.thursday'),
                  t('trainerProfile:days.friday'),
                  t('trainerProfile:days.saturday'),
                  t('trainerProfile:days.sunday')
                ];
                const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                return dayNames.indexOf(a.day) - dayNames.indexOf(b.day);
              })
              .map((slot, index) => (
                <Grid size={{xs:12 , sm:6 , md:4}} key={index}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t(`trainerProfile:days.${slot.day.toLowerCase()}`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default TrainerProfilePage;